import { supabase } from './supabase';

/**
 * Thought interface
 */
export interface Thought {
  id: number;
  title: string | null;
  content: string;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  project_id: number;
  tenant_id: string;
}

/**
 * Get all thoughts for a project
 * @param projectId - Project ID
 * @returns Promise with array of thoughts
 */
export const getProjectThoughts = async (projectId: string | number): Promise<Thought[]> => {
  const { data, error } = await supabase
    .from('thoughts')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching thoughts:', error);
    throw error;
  }

  return data || [];
};

/**
 * Get a thought by ID
 * @param thoughtId - Thought ID
 * @returns Promise with thought data
 */
export const getThoughtById = async (thoughtId: string | number): Promise<Thought> => {
  const { data, error } = await supabase
    .from('thoughts')
    .select('*')
    .eq('id', thoughtId)
    .single();

  if (error) {
    console.error('Error fetching thought:', error);
    throw error;
  }

  return data;
};

/**
 * Create a new thought
 * @param projectId - Project ID
 * @param title - Thought title (optional)
 * @param content - Thought content (plain text)
 * @returns Promise with created thought
 */
export const createThought = async (
  projectId: string | number,
  title: string | null = null,
  content: string = '',
  is_favorite: boolean = false
): Promise<Thought> => {
  const { data, error } = await supabase
    .from('thoughts')
    .insert([
      {
        title,
        content,
        is_favorite,
        project_id: projectId,
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating thought:', error);
    throw error;
  }

  return data;
};

/**
 * Update a thought
 * @param thoughtId - Thought ID
 * @param updates - Object with fields to update
 * @returns Promise with updated thought
 */
export const updateThought = async (
  thoughtId: string | number,
  updates: Partial<Omit<Thought, 'id' | 'created_at' | 'updated_at' | 'tenant_id'>>
): Promise<Thought> => {
  const { data, error } = await supabase
    .from('thoughts')
    .update(updates)
    .eq('id', thoughtId)
    .select()
    .single();

  if (error) {
    console.error('Error updating thought:', error);
    throw error;
  }

  return data;
};

/**
 * Delete a thought
 * @param thoughtId - Thought ID
 * @returns Promise with success status
 */
export const deleteThought = async (thoughtId: string | number): Promise<void> => {
  const { error } = await supabase
    .from('thoughts')
    .delete()
    .eq('id', thoughtId);

  if (error) {
    console.error('Error deleting thought:', error);
    throw error;
  }
};
